import { formatarDia } from '@common/i18n/formatosData';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type {
  EnderecoOficialApresentado,
  MinhaParticipacao,
  RespostaSalva,
} from '../types/Formulario';
import { BRASIL, linhasEndereco, linhasExterior, type Valores } from './formulario.utils';

/** O que a confirmação e o "Respondido" mostram (spec 33 R12, itens 1 a 4). */
export type Resumo = {
  /** `null`: não quis o brinde. */
  entrega: { linhas: string[]; exterior: boolean } | null;
  tamanho: string | null;
  /** `null`: o cadastro não muda. */
  cadastro: string[] | null;
};

/** O endereço do cadastro (fotografia) em linhas, no Brasil ou no exterior. */
export const cadastroEmLinhas = (o: EnderecoOficialApresentado) =>
  o.exterior
    ? linhasExterior({
        endereco: [o.endereco, o.numero, o.complemento, o.distrito].filter(Boolean).join(', '),
        cidade: o.cidadeNome,
        regiao: o.estadoNome,
        codigoPostal: o.cep,
        pais: o.paisNome,
      })
    : linhasEndereco({
        endereco: o.endereco,
        numero: o.numero,
        complemento: o.complemento,
        bairro: o.distrito,
        cidade: o.cidadeNome,
        uf: o.estadoSigla,
        cep: o.cep,
      });

/** Resumo da resposta salva ("Respondido", R16). */
export function resumoSalvo(r: RespostaSalva, foto: EnderecoOficialApresentado | null): Resumo {
  const e = r.entrega;
  const o = r.enderecoOficial;
  const entrega: Resumo['entrega'] =
    !r.desejaBrinde || !e
      ? null
      : e.origem === 'exterior'
        ? { linhas: linhasExterior(e), exterior: true }
        : {
            linhas:
              e.origem === 'cadastro' && !e.logradouro && foto
                ? cadastroEmLinhas(foto)
                : linhasEndereco({ ...e, endereco: e.logradouro }),
            exterior: false,
          };
  const cadastro = !o.desatualizado
    ? null
    : (o.paisId ?? 1) === 1
      ? linhasEndereco({
          endereco: o.endereco ?? null,
          numero: o.numero ?? null,
          complemento: o.complemento ?? null,
          bairro: o.distrito ?? null,
          cidade: o.cidadeNome ?? null,
          uf: o.estadoSigla ?? null,
          cep: o.cep ?? null,
        })
      : linhasExterior({
          endereco: [o.endereco, o.numero, o.complemento, o.distrito].filter(Boolean).join(', '),
          cidade: o.cidadeNome,
          regiao: o.regiao,
          codigoPostal: o.cep,
          pais: o.paisNome,
        });
  return { entrega, tamanho: r.desejaBrinde ? (r.tamanho?.tamanhoFinal ?? null) : null, cadastro };
}

/** Resumo do que está no formulário (confirmação, R12); `nomes` resolve os ids das listas do Axiis. */
export function resumoDosValores(
  v: Valores,
  atual: string | null,
  foto: EnderecoOficialApresentado | null,
  nomes: {
    uf: (estadoId: string) => string;
    cidade: (cidadeId: string) => string;
    pais: (paisId: string) => string;
  },
): Resumo {
  const quer = v.deseja === 'sim';
  const entrega: Resumo['entrega'] = !quer
    ? null
    : v.origem === 'exterior'
      ? { linhas: linhasExterior(v.exterior), exterior: true }
      : {
          linhas:
            v.origem === 'cadastro'
              ? foto
                ? cadastroEmLinhas(foto)
                : []
              : linhasEndereco({ ...v.entrega, endereco: v.entrega.logradouro }),
          exterior: false,
        };
  const o = v.oficial;
  const cadastro = !v.oficialMarcado
    ? null
    : o.paisId === BRASIL
      ? linhasEndereco({
          endereco: o.endereco,
          numero: o.numero,
          complemento: o.complemento,
          bairro: o.distrito,
          cidade: nomes.cidade(o.cidadeId),
          uf: nomes.uf(o.estadoId),
          cep: o.cep,
        })
      : linhasExterior({
          endereco: [o.endereco, o.numero, o.complemento, o.distrito].filter(Boolean).join(', '),
          cidade: o.cidade,
          regiao: o.regiao,
          codigoPostal: o.cep,
          pais: nomes.pais(o.paisId),
        });
  const tamanho = !quer ? null : atual && v.decisao === 'manter' ? atual : v.tamanho || null;
  return { entrega, tamanho, cadastro };
}

const rotulo = 'text-sm font-medium text-foreground-muted';

function Linhas({ linhas }: { linhas: string[] }) {
  return (
    <div className="flex flex-col break-words">
      {linhas.map((l, i) => (
        // As linhas podem se repetir (ex.: cidade e região iguais).
        // biome-ignore lint/suspicious/noArrayIndexKey: lista estática, sem reordenação
        <span key={i}>{l}</span>
      ))}
    </div>
  );
}

/** Previsão de entrega (R12, item 2): só no Brasil e com data ou janela; no exterior, o aviso do exterior. */
function Previsao({
  campanha,
  exterior,
}: {
  campanha: MinhaParticipacao['campanha'];
  exterior: boolean;
}) {
  const { t } = useTranslation('formulario');
  const datas = campanha.entregaPrevistaInicio && campanha.entregaPrevistaFim;
  if (!exterior && !datas && !campanha.janelaEnvio) return null;
  return (
    <div className="flex flex-col gap-1">
      {!exterior && <span className={rotulo}>{t('confirmar.previsao')}</span>}
      <p className="text-sm text-foreground-muted">
        <Trans
          ns="formulario"
          i18nKey={
            exterior ? 'entrega.avisoExterior' : datas ? 'entrega.avisoDatas' : 'entrega.aviso'
          }
          // `values` é tipado pela união das variáveis de todas as chaves do namespace.
          values={
            {
              janela: campanha.janelaEnvio ?? '',
              inicio: datas ? formatarDia(campanha.entregaPrevistaInicio as string) : '',
              fim: datas ? formatarDia(campanha.entregaPrevistaFim as string) : '',
            } as never
          }
          components={{ strong: <strong className="font-semibold text-foreground" /> }}
        />
      </p>
    </div>
  );
}

/** Itens 1 a 4 da R12. `acaoCadastro` é o "Alterar" ou o "Corrigir o endereço do cadastro" da confirmação. */
export function ResumoResposta({
  resumo,
  campanha,
  acaoCadastro,
}: {
  resumo: Resumo;
  campanha: MinhaParticipacao['campanha'];
  acaoCadastro?: ReactNode;
}) {
  const { t } = useTranslation('formulario');
  const { entrega, tamanho, cadastro } = resumo;
  return (
    // Entrega, tamanho e cadastro em colunas a partir de `sm`; a previsão numa linha embaixo.
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <div className="flex flex-col gap-1">
        <span className={rotulo}>{t('resumo.entrega')}</span>
        {entrega ? (
          <div className="font-medium text-foreground">
            <Linhas linhas={entrega.linhas} />
          </div>
        ) : (
          <p className="font-medium text-foreground">{t('confirmar.semBrinde')}</p>
        )}
      </div>
      {tamanho && (
        <div className="flex flex-col gap-1">
          <span className={rotulo}>{t('resumo.tamanho')}</span>
          <span className="text-foreground">{tamanho}</span>
          {entrega?.exterior && (
            <p className="text-sm text-foreground-muted">{t('camiseta.exteriorHint')}</p>
          )}
        </div>
      )}
      <div className="flex flex-col gap-1">
        <span className={rotulo}>{t('confirmar.cadastro')}</span>
        {cadastro ? <Linhas linhas={cadastro} /> : <span>{t('resumo.oficialSem')}</span>}
        {acaoCadastro && <div className="pt-1">{acaoCadastro}</div>}
      </div>
      {entrega && (
        <div className="sm:col-span-2 lg:col-span-3">
          <Previsao campanha={campanha} exterior={entrega.exterior} />
        </div>
      )}
    </div>
  );
}
